import { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Integration {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  configured_fields: string[];
  is_connected: boolean;
  last_synced_at: string | null;
}

export default function Integrations() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, string>>({});

  useEffect(() => { fetchIntegrations(); }, []);

  const fetchIntegrations = async () => {
    try {
      const res = await api.get('/api/integrations');
      setIntegrations(res.data.integrations);
    } catch { setIntegrations([]); } finally { setLoading(false); }
  };

  const connect = async (id: string) => {
    try {
      await api.post(`/api/integrations/${id}/connect`, { config: configValues });
      setConfiguring(null);
      setConfigValues({});
      fetchIntegrations();
    } catch { /* silent */ }
  };

  const disconnect = async (id: string) => {
    try {
      await api.delete(`/api/integrations/${id}/disconnect`);
      fetchIntegrations();
    } catch { /* silent */ }
  };

  const startConfiguring = (integration: Integration) => {
    setConfiguring(integration.id);
    const defaults: Record<string, string> = {};
    integration.configured_fields.forEach((f) => { defaults[f] = ''; });
    setConfigValues(defaults);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Integrations</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Connect third-party services</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="text-gray-500 dark:text-gray-400">Loading...</div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {integrations.map((intg) => (
            <div key={intg.id} className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{intg.icon}</span>
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">{intg.name}</h3>
                    <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">{intg.category}</span>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${intg.is_connected ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  {intg.is_connected ? 'Connected' : 'Not Connected'}
                </span>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">{intg.description}</p>

              {configuring === intg.id ? (
                <div className="mt-4 space-y-2">
                  {intg.configured_fields.map((field) => (
                    <div key={field}>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{field.replace(/_/g, ' ')}</label>
                      <input
                        type={field.includes('key') || field.includes('token') || field.includes('secret') ? 'password' : 'text'}
                        value={configValues[field] || ''}
                        onChange={(e) => setConfigValues({ ...configValues, [field]: e.target.value })}
                        placeholder={field.replace(/_/g, ' ')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                      />
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <button onClick={() => connect(intg.id)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm">
                      Connect
                    </button>
                    <button onClick={() => setConfiguring(null)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  {intg.is_connected ? (
                    <div className="flex items-center gap-2">
                      {intg.last_synced_at && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Last synced {new Date(intg.last_synced_at).toLocaleDateString()}</span>
                      )}
                      <button onClick={() => disconnect(intg.id)} className="px-3 py-1 border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded text-sm hover:bg-red-50 dark:hover:bg-red-900/20">
                        Disconnect
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => startConfiguring(intg)} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm w-full">
                      Connect
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
